import { supabase } from '../lib/supabase';
import { RoteiroOperacionalAtivo, OperacionalStats, OperacionalFiltro, ParadaOperacionalDetag } from '../types/operacional';
import { roteiroService } from './roteiroService';
import { ordemServicoService } from './ordemServicoService';
import { rastreamentoNotificacaoService } from './rastreamentoNotificacaoService';
import { sanitizeDeep } from '../utils/textSanitizer';

/**
 * Função utilitária para obter coordenadas determinísticas em SP/Região Metropolitana
 * com base no texto do endereço ou ID real, caso a parada não possua latitude/longitude no banco.
 */
function getCoordinatesForAddress(address: string | undefined, id: number): { lat: number; lng: number } {
  if (!address || address.trim().length === 0) {
    return { lat: -23.55052 + (id % 10) * 0.004, lng: -46.633308 + (id % 7) * 0.004 };
  }
  let hash = 0;
  for (let i = 0; i < address.length; i++) {
    hash = (hash << 5) - hash + address.charCodeAt(i);
    hash |= 0;
  }
  const latOffset = ((Math.abs(hash) % 180) - 90) / 2000;
  const lngOffset = ((Math.abs(hash * 17) % 180) - 90) / 2000;
  return {
    lat: -23.55052 + latOffset,
    lng: -46.633308 + lngOffset
  };
}

export const operacionalService = {
  /**
   * Busca SOMENTE os roteiros que foram explicitamente criados no sistema e que já foram INICIADOS
   * pelo motorista ao fazer login e iniciar a viagem (exclui roteiros apenas planejados/pendentes e OS avulsas).
   */
  async getRoteirosOperacionaisAtivos(): Promise<RoteiroOperacionalAtivo[]> {
    try {
      const hojeStr = new Date().toLocaleDateString('sv-SE'); // Formato 'YYYY-MM-DD' em horário local

      // 1. Busca do banco: OS do dia, Roteiros do dia, Pessoas, Veículos e GPS em tempo real
      const [
        { data: osBanco },
        { data: rotBanco },
        { data: pessoas },
        { data: veiculos },
        posicoesGpsMap
      ] = await Promise.all([
        supabase.from('ordem_de_servico').select('*').gte('data_atendimento', hojeStr).lte('data_atendimento', hojeStr),
        supabase.from('roteiro_motorista').select('*, roteiro_item(*)').gte('data_roteiro', hojeStr).lte('data_roteiro', hojeStr),
        supabase.from('pessoa').select('*'),
        supabase.from('veiculos').select('*'),
        rastreamentoNotificacaoService.getUltimasLocalizacoes()
      ]);

      const pessoasMap = new Map((pessoas || []).map(p => [p.id, p]));
      const veiculosMap = new Map((veiculos || []).map(v => [String(v.id), v]));

      // Fallback/Complemento caso a busca por filtro de data retorne vazia
      let ordensDoDia = (osBanco || []).filter(os => os.status_id !== 3);
      if (ordensDoDia.length === 0) {
        const todasOS = await ordemServicoService.getAll();
        ordensDoDia = (todasOS || []).filter(os => {
          if (os.status_id === 3) return false;
          const dt = os.data_atendimento ? os.data_atendimento.split('T')[0] : (os.created_at ? os.created_at.split('T')[0] : '');
          return dt === hojeStr;
        });
      }

      let roteirosDoDia = rotBanco || [];
      if (roteirosDoDia.length === 0) {
        const todosRoteiros = await roteiroService.getRoteirosMotorista();
        roteirosDoDia = (todosRoteiros || []).filter(r => {
          if (r.status === 'cancelado') return false;
          const dt = r.data_roteiro ? r.data_roteiro.split('T')[0] : '';
          return dt === hojeStr;
        });
      }

      // Mapeia OSs que já estão incluídas em roteiros formais para evitar duplicidade
      const osEmRoteiroIds = new Set<number>();
      roteirosDoDia.forEach(r => {
        (r.roteiro_item || r.itens || []).forEach((it: any) => {
          if (it.ordem_de_servico_id) osEmRoteiroIds.add(it.ordem_de_servico_id);
        });
      });

      const listaFinal: RoteiroOperacionalAtivo[] = [];

      // 1. Processa Roteiros Formais do dia
      roteirosDoDia.forEach(r => {
        const mot = pessoasMap.get(Number(r.motorista_id));
        const veic = r.veiculo_id ? veiculosMap.get(String(r.veiculo_id)) : (veiculos || []).find(v => v.placa === r.veiculo_placa);

        const paradas: ParadaOperacionalDetag[] = (r.roteiro_item || r.itens || []).map((it: any, pIdx: number) => {
          const endereco = it.endereco || 'Endereço não informado';
          const coords = (it.latitude && it.longitude)
            ? { lat: it.latitude, lng: it.longitude }
            : getCoordinatesForAddress(endereco, it.id || pIdx + 1);

          return {
            id: it.id || pIdx + 1,
            roteiro_id: r.id,
            ordem_de_servico_id: it.ordem_de_servico_id,
            ordem_sequencia: it.ordem_sequencia || pIdx + 1,
            tipo_parada: it.tipo_parada || 'entrega',
            status_parada: it.status_parada || 'pendente',
            cliente_nome: it.cliente_nome || `OS #${it.ordem_de_servico_id}`,
            endereco,
            latitude: coords.lat,
            longitude: coords.lng,
            contato_local: it.contato_local || '',
            telefone_local: it.telefone_local || '',
            horario_janela_inicio: it.horario_janela_inicio,
            horario_janela_fim: it.horario_janela_fim,
            hora_chegada: it.hora_chegada,
            hora_conclusao: it.hora_conclusao,
            descricao_carga: it.descricao_carga || '',
            valor_repasse: Number(it.valor_repasse) || 0
          };
        });

        const concluidas = paradas.filter(p => p.status_parada === 'concluida').length;
        const pct = paradas.length > 0 ? Math.round((concluidas / paradas.length) * 100) : 0;
        const paradaAtual = paradas.find(p => p.status_parada !== 'concluida') || paradas[0];

        const gpsReal = posicoesGpsMap.get(Number(r.motorista_id));
        let posLat = gpsReal?.latitude || paradaAtual?.latitude || -23.55052;
        let posLng = gpsReal?.longitude || paradaAtual?.longitude || -46.633308;
        let velocidadeKmh = gpsReal?.velocidade ?? (pct < 100 ? 35 : 0);
        let bateriaPct = gpsReal?.bateria_percentual ?? undefined;
        // IMPORTANTE: só rotular como "tempo real" quando existe GPS de verdade
        // (rastreamento_motorista). Sem isso, a posição é só uma estimativa
        // baseada na parada/endereço — não deve ser confundida com GPS ao vivo.
        let ultimaAtualizacao = gpsReal ? 'Em tempo real' : 'Sem sinal de GPS (posição estimada)';

        if (gpsReal?.created_at) {
          const diffMin = Math.floor((Date.now() - new Date(gpsReal.created_at).getTime()) / 60000);
          if (diffMin <= 1) ultimaAtualizacao = 'GPS ao vivo (Agora)';
          else if (diffMin < 60) ultimaAtualizacao = `GPS há ${diffMin} min`;
          else ultimaAtualizacao = `GPS há ${Math.floor(diffMin / 60)}h`;
        }

        listaFinal.push({
          id: r.id,
          nome_roteiro: r.nome_roteiro || `Roteiro #${r.id}`,
          data_roteiro: r.data_roteiro || hojeStr,
          status: (r.status as any) || 'em_execucao',
          motorista_id: r.motorista_id,
          motorista_nome: mot?.nome || mot?.nome_fantasia || r.motorista_nome || `Motorista #${r.motorista_id}`,
          motorista_telefone: mot?.fone || mot?.email || '',
          status_motorista: pct === 100 ? 'disponivel' : 'em_transito',
          veiculo_id: String(r.veiculo_id || ''),
          veiculo_placa: veic?.placa || r.veiculo_placa || 'Sem Placa',
          veiculo_modelo: veic?.modelo || 'Veículo Operacional',
          veiculo_tipo: veic?.tipo_veiculo || '',
          posicao_atual: {
            lat: posLat,
            lng: posLng,
            velocidade_kmh: velocidadeKmh,
            bateria_pct: bateriaPct,
            ultima_atualizacao: ultimaAtualizacao,
            endereco_aproximado: paradaAtual?.endereco || 'Em deslocamento'
          },
          distancia_total_km: r.distancia_estimada_km || Math.max(10, paradas.length * 8),
          tempo_estimado_min: r.tempo_estimado_minutos || Math.max(30, paradas.length * 25),
          paradas_concluidas: concluidas,
          paradas_totais: paradas.length,
          percentual_concluido: pct,
          paradas
        });
      });

      // 2. Processa OSs do dia direcionadas aos motoristas
      ordensDoDia.forEach(os => {
        if (osEmRoteiroIds.has(os.id)) return;

        const veic = os.veiculos_id ? veiculosMap.get(String(os.veiculos_id)) : undefined;
        const driverId = os.agregado
          ? Number(os.agregado)
          : (os.motorista_id
              ? Number(os.motorista_id)
              : (veic?.motorista_id ? Number(veic.motorista_id) : (veic?.agregado_id ? Number(veic.agregado_id) : undefined)));

        const mot = driverId ? pessoasMap.get(driverId) : undefined;

        const isConcluido = os.status_id === 4 || os.status_id === 6 || os.status_id === 7 || !!os.assinatura;
        const isEmAndamento = os.status_id === 2;

        const enderecoColeta = os.retirada || 'Origem não informada';
        const enderecoEntrega = os.entrega || 'Destino não informado';

        const coordsColeta = getCoordinatesForAddress(enderecoColeta, os.id * 10 + 1);
        const coordsEntrega = getCoordinatesForAddress(enderecoEntrega, os.id * 10 + 2);

        const paradas: ParadaOperacionalDetag[] = [
          {
            id: os.id * 10 + 1,
            roteiro_id: os.id + 1000000,
            ordem_de_servico_id: os.id,
            ordem_sequencia: 1,
            tipo_parada: 'coleta',
            status_parada: isConcluido || isEmAndamento ? 'concluida' : 'pendente',
            cliente_nome: 'Coleta / Retirada',
            endereco: enderecoColeta,
            latitude: coordsColeta.lat,
            longitude: coordsColeta.lng,
            descricao_carga: os.descricao || '',
            valor_repasse: Number(os.repasse) || 0
          },
          {
            id: os.id * 10 + 2,
            roteiro_id: os.id + 1000000,
            ordem_de_servico_id: os.id,
            ordem_sequencia: 2,
            tipo_parada: 'entrega',
            status_parada: isConcluido ? 'concluida' : (isEmAndamento ? 'em_deslocamento' : 'pendente'),
            cliente_nome: os.descricao || `OS #${os.id}`,
            endereco: enderecoEntrega,
            latitude: coordsEntrega.lat,
            longitude: coordsEntrega.lng,
            descricao_carga: os.descricao || '',
            valor_repasse: Number(os.repasse) || 0
          }
        ];

        const concluidas = paradas.filter(p => p.status_parada === 'concluida').length;
        const pct = isConcluido ? 100 : (isEmAndamento ? 50 : 0);
        const paradaAtual = paradas.find(p => p.status_parada !== 'concluida') || paradas[1];

        const gpsReal = driverId ? posicoesGpsMap.get(driverId) : undefined;
        let posLat = gpsReal?.latitude || paradaAtual?.latitude || coordsEntrega.lat;
        let posLng = gpsReal?.longitude || paradaAtual?.longitude || coordsEntrega.lng;
        let velocidadeKmh = gpsReal?.velocidade ?? (isEmAndamento ? 35 : 0);
        let bateriaPct = gpsReal?.bateria_percentual ?? undefined;
        // IMPORTANTE: só rotular como "tempo real" quando existe GPS de verdade
        // (rastreamento_motorista). Sem isso, a posição é só uma estimativa
        // baseada na parada/endereço — não deve ser confundida com GPS ao vivo.
        let ultimaAtualizacao = gpsReal ? 'Em tempo real' : 'Sem sinal de GPS (posição estimada)';

        if (gpsReal?.created_at) {
          const diffMin = Math.floor((Date.now() - new Date(gpsReal.created_at).getTime()) / 60000);
          if (diffMin <= 1) ultimaAtualizacao = 'GPS ao vivo (Agora)';
          else if (diffMin < 60) ultimaAtualizacao = `GPS há ${diffMin} min`;
          else ultimaAtualizacao = `GPS há ${Math.floor(diffMin / 60)}h`;
        }

        listaFinal.push({
          id: os.id + 1000000,
          nome_roteiro: `OS #${os.id} - ${os.descricao || 'Transporte de Carga'}`,
          data_roteiro: os.data_atendimento || hojeStr,
          status: isConcluido ? 'concluido' : (isEmAndamento ? 'em_execucao' : 'planejado'),
          motorista_id: driverId || 0,
          motorista_nome: mot ? (mot.nome || mot.nome_fantasia) : (driverId ? `Motorista #${driverId}` : 'Sem motorista atribuído'),
          motorista_telefone: mot ? (mot.fone || mot.email || '') : '',
          status_motorista: isConcluido ? 'disponivel' : (isEmAndamento ? 'em_transito' : 'disponivel'),
          veiculo_id: String(os.veiculos_id || ''),
          veiculo_placa: veic?.placa || os.placa || 'Sem Placa',
          veiculo_modelo: veic?.modelo || 'Veículo Operacional',
          veiculo_tipo: veic?.tipo_veiculo || '',
          posicao_atual: {
            lat: posLat,
            lng: posLng,
            velocidade_kmh: velocidadeKmh,
            bateria_pct: bateriaPct,
            ultima_atualizacao: ultimaAtualizacao,
            endereco_aproximado: paradaAtual?.endereco || 'Em deslocamento'
          },
          distancia_total_km: 15,
          tempo_estimado_min: 40,
          paradas_concluidas: concluidas,
          paradas_totais: 2,
          percentual_concluido: pct,
          paradas
        });
      });

      return sanitizeDeep(listaFinal);
    } catch (err) {
      console.error('Erro ao carregar roteiros operacionais ativos:', err);
      return [];
    }
  },

  /**
   * Calcula estatísticas dos roteiros operacionais
   */
  calcularEstatisticas(roteiros: RoteiroOperacionalAtivo[]): OperacionalStats {
    let paradasConcluidas = 0;
    let paradasPendentes = 0;
    let distanciaTotal = 0;
    let ocorrencias = 0;
    let veiculosEmMovimento = 0;

    roteiros.forEach(r => {
      paradasConcluidas += r.paradas_concluidas;
      paradasPendentes += (r.paradas_totais - r.paradas_concluidas);
      distanciaTotal += r.distancia_total_km;
      ocorrencias += (r.ocorrencias_count || 0);
      if (r.posicao_atual && (r.posicao_atual.velocidade_kmh || 0) > 0) {
        veiculosEmMovimento++;
      }
    });

    return {
      motoristas_em_rota: roteiros.length,
      veiculos_em_movimento: veiculosEmMovimento,
      paradas_concluidas_hoje: paradasConcluidas,
      paradas_pendentes_hoje: paradasPendentes,
      distancia_total_km: Math.round(distanciaTotal * 10) / 10,
      alertas_ocorrencias: ocorrencias
    };
  },

  /**
   * Filtra os roteiros com base nos parâmetros
   */
  filtrarRoteiros(roteiros: RoteiroOperacionalAtivo[], filtro: OperacionalFiltro): RoteiroOperacionalAtivo[] {
    return roteiros.filter(r => {
      const termo = filtro.busca.toLowerCase().trim();
      const bateuBusca = !termo || 
        r.motorista_nome.toLowerCase().includes(termo) ||
        r.veiculo_placa.toLowerCase().includes(termo) ||
        r.nome_roteiro.toLowerCase().includes(termo) ||
        r.paradas.some(p => p.cliente_nome.toLowerCase().includes(termo) || p.endereco.toLowerCase().includes(termo));

      const bateuStatusMotorista = filtro.status_motorista === 'todos' || r.status_motorista === filtro.status_motorista;
      const bateuStatusRoteiro = filtro.status_roteiro === 'todos' || r.status === filtro.status_roteiro;

      return bateuBusca && bateuStatusMotorista && bateuStatusRoteiro;
    });
  }
};
